import React, { useEffect, useRef, useState } from "react";
import { Document, Page } from "react-pdf";
import CustomButton from "../components/Button";
import { EDITOR_PAGE_WIDTH } from "./cpFieldRules";
import { previewLabel } from "./signAllCopy";

// Tailwind's lg breakpoint: from here up the preview is a side panel, below it a full-screen overlay.
const WIDE = "(min-width: 1024px)";
export const isWideScreen = () => Boolean(window.matchMedia?.(WIDE)?.matches);
// At lg the Layout's header and footer take about 9.5rem, and the page's padding the rest of the 13rem.
const SIDE_PANEL = "lg:sticky lg:inset-auto lg:top-6 lg:z-auto lg:flex lg:h-[calc(100vh-13rem)] lg:rounded lg:border lg:border-gray-200";

// Pages render at the panel's width; where ResizeObserver is missing they keep the editor's page width.
function useContentWidth(ref) {
  const [width, setWidth] = useState(EDITOR_PAGE_WIDTH);
  useEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(([entry]) => {
      const next = Math.floor(entry.contentRect.width);
      // A hidden overlay measures 0, so keep the last width until it's shown again.
      if (next > 0) setWidth(next);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

// ENG-1865: the open agreement's pages, beside the list on a wide screen and over it on a narrow one.
const SignAllPreview = ({ agreement, preview, pageCount, error, onLoaded, onClose, closeDisabled }) => {
  const headerRef = useRef(null);
  const bodyRef = useRef(null);
  const width = useContentWidth(bodyRef);
  const open = Boolean(agreement);
  const openSecret = agreement?.secret;

  // Below lg the overlay covers the list, so focus moves into it.
  useEffect(() => {
    if (openSecret && !isWideScreen()) headerRef.current?.querySelector("button")?.focus();
  }, [openSecret]);

  useEffect(() => {
    if (!open || closeDisabled) return undefined;
    const onKeyDown = (event) => {
      // On a wide screen the panel covers nothing, and an Escape pressed in the signature dialog mustn't close it.
      if (event.key === "Escape" && !isWideScreen()) onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, closeDisabled, onClose]);

  return (
    <section
      data-testid="sign-all-preview"
      aria-label={open ? previewLabel(agreement.label) : undefined}
      className={`${open ? "fixed inset-0 z-50 flex" : "hidden"} flex-col bg-white ${SIDE_PANEL}`}
    >
      {open && (
        <div ref={headerRef} className="flex items-center gap-3 border-b border-gray-200 px-4 py-2">
          <h3 className="min-w-0 flex-1 truncate font-medium">{agreement.label}</h3>
          <CustomButton size="sm" labelColor="black" label="Close" className="shrink-0" disabled={closeDisabled} onClick={onClose} />
        </div>
      )}
      <div ref={bodyRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 [scrollbar-gutter:stable]">
        {!open ? (
          <p className="flex h-full items-center justify-center text-center text-sm text-gray-600">Open an agreement to read it here.</p>
        ) : error ? (
          <p className="text-sm text-red-700">{error}</p>
        ) : preview?.secret === agreement.secret ? (
          <Document key={agreement.secret} file={preview.file} onLoadSuccess={({ numPages }) => onLoaded(agreement.secret, numPages)}>
            {Array.from({ length: pageCount }, (_, page) => (
              <Page key={page} pageNumber={page + 1} width={width} className="mb-4 shadow" />
            ))}
          </Document>
        ) : (
          <p className="text-sm text-gray-600">Loading…</p>
        )}
      </div>
    </section>
  );
};

export default SignAllPreview;
